import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { midTeal, charcoal } from '../fj-styles/colors';

const dotFrames = keyframes`
  0% {
      width: 4px;
      height: 4px;
      z-index: 2;
  }
  25% {
      width: 8px;
      height: 8px;
      margin-left: 20px;
      z-index: 2;
  }
  50% {
      width: 12px;
      height: 12px;
      margin-left: 40px;
      z-index: 3;
  }
  75% {
      width: 14px;
      height: 14px;
      margin-left: 20px;
      z-index: 3;
  }
  100% {
      width: 6px;
      height: 6px;
      margin-left: 0px;
      z-index: 2;
  }
`;

const dotSecondaryFrames = keyframes`
  0% {
      width: 12px;
      height: 12px;
      margin-left: 40px;
      z-index: 3;
  }
  25% {
      width: 14px;
      height: 14px;
      margin-left: 20px;
      z-index: 3;
  }
  50% {
      width: 4px;
      height: 4px;
      margin-left: 0px;
      z-index: 2;
  }
  75% {
      width: 8px;
      height: 8px;
      margin-left: 20px;
      z-index: 2;
  }
  100% {
      width: 12px;
      height: 12px;
      margin-left: 40px;
      z-index: 3;
  }
`;

const bondFrames = keyframes`
  0% {
      width: 40px;
  }
  25% {
      width: 20px;
  }
  50% {
      width: 4px;
  }
  75% {
      width: 20px;
  }
  100% {
      width: 40px;
  }
`;

const Wrapper = styled.div<{ center?: boolean }>`
  margin: 15px;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  ${({ center }) =>
    center &&
    css`
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
    `};
`;

const Strand = styled.div`
  display: flex;
  flex-flow: row nowrap;
  position: relative;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  width: 50px;
`;

const Dot = styled.div<{ color: string; i: number }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  margin-right: 40px;
  position: absolute;
  left: 0;
  transition: all 500ms ease;
  z-index: 2;
  animation: ${({ i }) => css`
    ${dotFrames} 1.5s linear infinite ${-0.2 * i}s
  `};
`;

const DotSecondary = styled(Dot)`
  margin-left: 40px;
  transition: none;
  animation: ${({ i }) => css`
    ${dotSecondaryFrames} 1.5s linear infinite ${-0.2 * i}s
  `};
`;

const Bond = styled.div<{ color: string; i: number }>`
  width: 50px;
  height: 2px;
  background: ${({ color }) => color};
  position: absolute;
  z-index: 1;
  animation: ${({ i }) => css`
    ${bondFrames} 750ms linear infinite ${-0.2 * i}s
  `};
`;

interface HelixProps {
  numberOfStrands?: number;
  center?: boolean;
}

const Helix = ({ numberOfStrands = 6, center = false }: HelixProps): React.JSX.Element => {
  const dotColor = midTeal.hex();
  const bondColor = charcoal.hex();

  const strands = Array.from(Array(numberOfStrands)).map((_, i) => (
    <Strand key={`strand-${i}`}>
      <Dot i={i} color={dotColor} />
      <Bond i={i} color={bondColor} />
      <DotSecondary i={i} color={dotColor} />
    </Strand>
  ));

  return <Wrapper center={center}>{strands}</Wrapper>;
};

export default Helix;
